import { Injectable } from '@angular/core';
import { Airport } from '../../models/airport';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { TxService } from '../shared/tx.service';
import { HelperService } from '../helper/helper.service';
import { AccountService } from '../user/account.service';
import { ApiErrorService } from '../api-error.service';
import { Airline } from 'src/app/models/airline';
import { SmartHttpService } from '../smart-http/smart-http.service';
import { LocalStorageService } from '../localStorage/localStorage.service';
import { localStorageKeys } from '../localStorage/localStorageVariables';
import {
  normalizeAirportList,
  readStoredLatLong,
  serviceTypeToAirportListType,
} from './airport-list.util';

interface AirportAPIResponse {
  status: number;
  result: Airport[];
}
interface AirlineAPIResponse {
  status: number;
  result: Airline[];
}

@Injectable({
  providedIn: 'root',
})
export class AirportListService {
  selectedAirport = new Subject<string>();
  apiParams: any;

  // eslint-disable-next-line max-len
  constructor(
    private http: SmartHttpService,
    private envService: TxService,
    private helperService: HelperService,
    private accountService: AccountService,
    private apiErrorService: ApiErrorService,
    private localStorageData: LocalStorageService
  ) {
    this.initApiParams();
    if (this.localStorageData.getItem(localStorageKeys.SELECTED_AIRPORT_CODE)) {
      this.selectedAirport.next(
        this.localStorageData.getItem(localStorageKeys.SELECTED_AIRPORT_CODE)
      );
    }
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  airportApi =
    'https://api2.travelx.ai/api/getAirportList?&key=palsgcvgscvvs&pid=smt';

  public setSelectedAirport(value: string) {
    this.localStorageData.setItem(
      localStorageKeys.SELECTED_AIRPORT_CODE,
      value
    );
    this.selectedAirport.next(value);
  }

  public getSelectedAirport(): string {
    return this.localStorageData.getItem(
      localStorageKeys.SELECTED_AIRPORT_CODE
    );
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  public get retailDetailServiceApi(): string {
    const finalRequestParams = {
      ...this.envService.getDefaultApiParams(),
      ...this.apiParams,
    };
    // eslint-disable-next-line max-len
    return (this.retailDetailServiceApi = `${this.envService.getRetailersDetailsV2Api()}?${this.helperService.serializeToGetParams(
      finalRequestParams
    )}`);
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  public set retailDetailServiceApi(value: string) {}

  // eslint-disable-next-line @typescript-eslint/member-ordering
  public get airportMapServiceApi(): string {
    const finalRequestParams = {
      ...this.envService.getDefaultApiParams(),
      ...this.apiParams,
    };
    // eslint-disable-next-line max-len
    return (this.airportMapServiceApi = `${this.envService.getAirportMapsApi()}?${this.helperService.serializeToGetParams(
      finalRequestParams
    )}`);
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  public set airportMapServiceApi(value: string) {}

  initApiParams() {
    this.apiParams = {
      page: 1,
      perpage: 10,
      city: '',
    };
  }

  getAllAirports(
    searchTerm: string = '',
    serviceType: string = '',
    page: number = 1,
    count: number = 10,
    fareType: any = null
  ) {
    this.initApiParams();
    this.apiParams.page = page;
    this.apiParams.perpage = count;
    if (fareType) {
      this.apiParams.fareType = fareType;
    }
    // getAirportList sorts by proximity when given the user's coordinates;
    // they are sent whenever location has been captured, omitted otherwise.
    const coords = readStoredLatLong(
      this.localStorageData.getItem(localStorageKeys.LAT_LONG_DATA)
    );
    // Dutyfree, meet & greet, porter and airport companion are scoped with the
    // endpoint's own `type`; other categories keep `servicetype` alone.
    const listType = serviceTypeToAirportListType(serviceType);
    const finalRequestParams = {
      airport: searchTerm.trim(),
      servicetype: serviceType,
      ...(listType ? { type: listType } : {}),
      ...this.apiParams,
      ...(coords ? { latitude: coords.latitude, longitude: coords.longitude } : {}),
      ...this.envService.getDefaultApiParams(),
    };
    // eslint-disable-next-line max-len
    return this.http
      .get<AirportAPIResponse>(
        `${this.envService.getAirportServicesGetAirportListApi()}?${this.helperService.serializeToGetParams(
          finalRequestParams
        )}`
      )
      .pipe(
        // getAirportList names the airport `title`; callers here read
        // `airportName`, so both are filled in before the list is handed on.
        map((res: any) => ({
          ...res,
          result: normalizeAirportList(res?.result ?? res?.results),
        }))
      );
  }

  getAllAirlines(
    searchTerm: string = '',
    page: number = 1,
    count: number = 20
  ) {
    this.apiParams.page = page;
    this.apiParams.perpage = count;
    const finalRequestParams = {
      q: searchTerm.trim(),
      ...this.apiParams,
      ...this.envService.getDefaultApiParams(),
    };
    // eslint-disable-next-line max-len
    return this.http.getWithErorrHandler<AirlineAPIResponse>(
      `${this.envService.getAirlineListApi()}?${this.helperService.serializeToGetParams(
        finalRequestParams
      )}`
    );
  }

  getRetailerDetailsByStoreId(
    storeId: number,
    page: number,
    location: number,
    prefix: string,
    airportCode: string,
  ): Observable<any> {
    this.initApiParams();
    this.apiParams.storeId = storeId;
    this.apiParams.airport = airportCode || this.localStorageData.getItem(
      localStorageKeys.SELECTED_AIRPORT_CODE
    );
    this.apiParams.page = page;
    this.apiParams.locationid = location;
    this.apiParams.showcoupon = 1;
    // this.apiParams.serviceType = 'dutyfree';
    this.apiParams.prefix = prefix || '';
    return this.http.getWithErorrHandler(this.retailDetailServiceApi);
  }
  // /api/getAirportMaps?key=qw1op5cv8j0d&pid=gmr&airport=HYD
  getAirportMap(): Observable<any> {
    this.initApiParams();
    this.apiParams.airport = this.localStorageData.getItem(
      localStorageKeys.SELECTED_AIRPORT_CODE
    );
    return this.http.getWithErorrHandler(this.airportMapServiceApi);
  }

  /*   getSelectedAirport() {

    return  'undefined' !== typeof this.selectedAirport ? this.selectedAirport : false;
  }
 */

  getAirportInfo(lat: number, long: number) {
    if (lat != null && long != null) {
      this.apiParams.lat = lat;
      this.apiParams.lon = long;
    }
    const finalRequestParams = {
      ...this.apiParams,
      ...this.envService.getDefaultApiParams(),
    };
    // eslint-disable-next-line max-len
    return this.http.getWithErorrHandler(
      `${this.envService.getAirportInfoApi()}?${this.helperService.serializeToGetParams(
        finalRequestParams
      )}`
    );
  }
}
